import { Dispatch } from 'redux'
import { useThunkActions } from '../useThunkActions'
import * as actions from './actions'
import { GlobalFilterVariant } from 'types/GlobalGlobalFilter'
import { ApplicationState } from 'redux/Stores/types'
import { selectAvailableUnits } from 'redux/selectors/AvailableUnits'
import { selectAll as selectAllCompanyGroups } from 'redux/selectors/CompanyGroups'
import {
  selectAccountSettings,
  selectSignedInAccount
} from 'redux/selectors/SignedInAccount'

/**
 * Derives the initial global filter from Redux state (signed-in account,
 * available units, company groups) and localStorage. Called from
 * initializeApp (redux/actions/AppInit) once the critical fetches are done.
 */
export function initializeGlobalFilterFromState() {
  return (dispatch: Dispatch, getState: () => ApplicationState) => {
    const state = getState()
    const accountSettings = selectAccountSettings(state)
    const [companyGroups] = selectAllCompanyGroups(state)

    if (!accountSettings || !accountSettings.account) {
      return
    }

    initializeGlobalFilter(
      accountSettings.account.id,
      selectAvailableUnits(state),
      companyGroups,
      accountSettings.isManagementCompany,
      accountSettings.allowAccess.internal_follow_up,
      accountSettings.isCompanyGroup
    )(dispatch)
  }
}

function initializeGlobalFilter(
  accountId: string,
  availableUnits: string[],
  companyGroups: { id: string }[],
  isManagementCompany: boolean,
  allowInternalFollowUp: boolean,
  isCompanyGroupUser: boolean
) {
  return (dispatch: Dispatch) => {
    // We do not support Global Filter for company group users.
    if (isCompanyGroupUser) {
      dispatch(
        actions.initialize({
          variant: GlobalFilterVariant.ALL,
          customSelected: [],
          companyGroupId: null
        })
      )
    } else {
      let variant = getStoredVariant(accountId)
      const storedCustomSelected = getStoredCustomSelected(accountId)
      const storedCompanyGroupId = getStoredCompanyGroupId(accountId)

      // Clean up customSelected - only keep available units
      const customSelected = storedCustomSelected.filter((item) =>
        availableUnits.includes(item)
      )

      // Check if company group still exists
      const companyGroupExists = companyGroups.some(
        (cg) => cg.id === storedCompanyGroupId
      )
      let companyGroupId = companyGroupExists ? storedCompanyGroupId : null

      // If only management company, default to COMPANY_GROUP
      if (isManagementCompany && !allowInternalFollowUp) {
        // If no valid company group is selected, pick the first one
        if (!companyGroupId && companyGroups.length > 0) {
          companyGroupId = companyGroups[0].id
        }

        // Set variant to COMPANY_GROUP if we have a valid selection
        if (companyGroupId) {
          variant = GlobalFilterVariant.COMPANY_GROUP
        } else {
          // Fallback to ALL if no company groups exist
          variant = GlobalFilterVariant.ALL
        }
      } else {
        // Normal flow - fallback to ALL if stored values are no longer valid
        if (
          variant === GlobalFilterVariant.CUSTOM &&
          customSelected.length === 0
        ) {
          variant = GlobalFilterVariant.ALL
        }

        if (variant === GlobalFilterVariant.COMPANY_GROUP && !companyGroupId) {
          variant = GlobalFilterVariant.ALL
        }
      }

      dispatch(
        actions.initialize({
          variant,
          customSelected,
          companyGroupId
        })
      )
    }
  }
}

function updateGlobalFilter(
  variant: GlobalFilterVariant,
  customSelected: string[],
  companyGroupId: string | null = null
) {
  return (dispatch: Dispatch, getState: () => ApplicationState) => {
    const account = selectSignedInAccount(getState())

    const filter: actions.TSetGlobalFilter = {
      variant,
      customSelected:
        variant === GlobalFilterVariant.CUSTOM ? customSelected : [],
      companyGroupId:
        variant === GlobalFilterVariant.COMPANY_GROUP ? companyGroupId : null
    }

    // Save to localStorage
    saveToLocalStorage(filter, account?.id)

    // Update Redux
    dispatch(actions.update(filter))
  }
}

export const useGlobalFilterActions = () => {
  return {
    updateGlobalFilter: useThunkActions(updateGlobalFilter)
  }
}

/** PRIVATE LOCAL STORAGE */
const getVariantKey = (accountId: string) => `globalFilter.variant.${accountId}`
const getCustomSelectedKey = (accountId: string) =>
  `globalFilter.customSelected.${accountId}`
const getCompanyGroupIdKey = (accountId: string) =>
  `globalFilter.companyGroupId.${accountId}`

function getStoredVariant(
  accountId = 'MISSING_ACCOUNT_ID'
): GlobalFilterVariant {
  const stored = localStorage.getItem(getVariantKey(accountId))

  if (
    stored === GlobalFilterVariant.ALL ||
    stored === GlobalFilterVariant.STANDARD ||
    stored === GlobalFilterVariant.CUSTOM ||
    stored === GlobalFilterVariant.COMPANY_GROUP
  ) {
    return stored as GlobalFilterVariant
  }

  return GlobalFilterVariant.ALL // default
}

function getStoredCustomSelected(accountId = 'MISSING_ACCOUNT_ID'): string[] {
  const stored = localStorage.getItem(getCustomSelectedKey(accountId))

  try {
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function getStoredCompanyGroupId(
  accountId = 'MISSING_ACCOUNT_ID'
): string | null {
  return localStorage.getItem(getCompanyGroupIdKey(accountId))
}

function saveToLocalStorage(
  filter: actions.TSetGlobalFilter,
  accountId = 'MISSING_ACCOUNT_ID'
): void {
  localStorage.setItem(getVariantKey(accountId), filter.variant)
  localStorage.setItem(
    getCustomSelectedKey(accountId),
    JSON.stringify(filter.customSelected)
  )

  if (filter.companyGroupId) {
    localStorage.setItem(getCompanyGroupIdKey(accountId), filter.companyGroupId)
  } else {
    localStorage.removeItem(getCompanyGroupIdKey(accountId))
  }
}
